import classnames from "classnames";
import { componentWrapper } from "../../../utilities";
import { Divider } from "../../divider";
import { Pill } from "../../pill";
import { Stack } from "../../stack";
import { Text } from "../../text";
import { Tooltip } from "../../tooltip";
import styles from "./variable-pill.module.scss";
import type { CustomVariablePillFactory, IProps } from "./variable-pill.types";

/**
 * Display-only pill for an Ask Spotlight variable: the standard `Pill` with a violet label, an optional kind
 * icon, a selection ring and an optional hover tooltip. It holds no variable state, so it also works outside the
 * composer, e.g. as the `subtle` variant inside a sent message bubble.
 * @param props.label The variable's display label.
 * @param props.icon Optional leading icon for the variable's kind.
 * @param props.tooltip Optional hover tooltip; its `items` go below a divider.
 * @param props.selected Whether to draw the selection ring, which also hides the tooltip. Defaults to false.
 * @param props.variant The `Pill` variant to draw on. Defaults to "default"; the message bubble uses "subtle".
 * @param props.size The `Pill` size. Defaults to "sm", which is what the composer draws.
 * @returns {JSX.Element} The VariablePill component.
 */
const VariablePill = componentWrapper<CustomVariablePillFactory, IProps>(
    "VariablePill",
    ({ label, icon, tooltip, selected = false, variant = "default", size = "sm", className, ...otherProps }, ref) => {
        const pill = (
            <Pill
                ref={ref}
                className={classnames(styles["root"], className)}
                variant={variant}
                size={size}
                leftIcon={icon}
                data-selected={selected}
                data-testid="variable-pill"
                {...otherProps}
            >
                {label}
            </Pill>
        );

        if (tooltip === undefined) {
            return pill;
        }

        const { prefix, title, items } = tooltip;
        const header = (
            <Text inherit>
                {prefix !== undefined && <Text span inherit dimmed>{`${prefix}: `}</Text>}
                {title}
            </Text>
        );

        const tooltipLabel =
            items === undefined ? (
                header
            ) : (
                <Stack gap="sm">
                    {header}
                    <Divider color="var(--border-transparent-default-2-color)" />
                    <Stack gap="xs">
                        {items.map((item, index) => (
                            <Text key={`${index}-${item}`} inherit>
                                {item}
                            </Text>
                        ))}
                    </Stack>
                </Stack>
            );

        return (
            <Tooltip label={tooltipLabel} disabled={selected}>
                {pill}
            </Tooltip>
        );
    }
);

export { VariablePill };
